import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Ban,
  Calendar,
  Check,
  CheckCircle2,
  Globe,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Settings2,
  ShieldAlert,
  Tag,
  ToggleLeft,
  Trash2,
  Type,
  UserCog,
  Users,
  XCircle,
} from "lucide-react";

import { AdminPage, Loading } from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  createAdminUser,
  deleteAdminUser,
  listAdminUsers,
  setUserPassword,
  blockIp,
  clearLoginAttempts,
  listBlockedIps,
  listLoginAttempts,
  unblockIp,
  listSettings,
  saveSetting,
} from "@/lib/api";

const searchSchema = z.object({
  tab: z.enum(["site", "utilizatori", "securitate"]).default("site"),
});

export const Route = createFileRoute("/_authenticated/admin/setari")({
  validateSearch: searchSchema,
  component: SettingsAdmin,
});

type FieldType = "text" | "textarea" | "email" | "tel" | "datetime-local" | "boolean";

type SettingMeta = {
  label: string;
  description: string;
  category: string;
  type: FieldType;
  icon: React.ReactNode;
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Eveniment: <Calendar className="h-4 w-4" />,
  Contact: <Mail className="h-4 w-4" />,
  Site: <Globe className="h-4 w-4" />,
  Înregistrare: <ToggleLeft className="h-4 w-4" />,
  Avansat: <Settings2 className="h-4 w-4" />,
};

const KNOWN_SETTINGS: Record<string, SettingMeta> = {
  site_title: {
    label: "Titlul site-ului",
    description: "Apari în tab-ul browserului și în meta-tagurile de SEO.",
    category: "Site",
    type: "text",
    icon: <Type className="h-4 w-4" />,
  },
  hero_badge: {
    label: "Badge hero",
    description: "Textul mic afișat deasupra titlului principal de pe prima pagină.",
    category: "Site",
    type: "text",
    icon: <Tag className="h-4 w-4" />,
  },
  event_date: {
    label: "Data și ora evenimentului",
    description: "Folosită în footer, hero și formularul de contact.",
    category: "Eveniment",
    type: "datetime-local",
    icon: <Calendar className="h-4 w-4" />,
  },
  event_location: {
    label: "Locația evenimentului",
    description: "Numele locației afișat în contact și footer.",
    category: "Eveniment",
    type: "text",
    icon: <MapPin className="h-4 w-4" />,
  },
  event_address: {
    label: "Adresa locației",
    description: "Adresa completă folosită în secțiunea de contact și footer.",
    category: "Eveniment",
    type: "textarea",
    icon: <MapPin className="h-4 w-4" />,
  },
  contact_email: {
    label: "Email de contact",
    description: "Adresa publică la care pot scrie participanții.",
    category: "Contact",
    type: "email",
    icon: <Mail className="h-4 w-4" />,
  },
  contact_phone: {
    label: "Telefon de contact",
    description: "Numărul afișat în footer și pagina de contact.",
    category: "Contact",
    type: "tel",
    icon: <Phone className="h-4 w-4" />,
  },
  registrations_open: {
    label: "Înregistrări deschise",
    description: "Când este activ, vizitatorii pot trimite formularul de înregistrare.",
    category: "Înregistrare",
    type: "boolean",
    icon: <ToggleLeft className="h-4 w-4" />,
  },
  registrations_closed_message: {
    label: "Mesaj înregistrări închise",
    description: "Textul afișat în locul formularului când înregistrările sunt oprite.",
    category: "Înregistrare",
    type: "textarea",
    icon: <Type className="h-4 w-4" />,
  },
};

function metaFor(key: string): SettingMeta {
  return (
    KNOWN_SETTINGS[key] ?? {
      label: key,
      description: "Setare personalizată.",
      category: "Avansat",
      type: "text",
      icon: <Settings2 className="h-4 w-4" />,
    }
  );
}

function SettingsAdmin() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/setari" });

  return (
    <AdminPage
      title="Setări"
      description="Configurează site-ul, conturile de administrator și securitatea accesului."
    >
      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({ search: { tab: v as "site" | "utilizatori" | "securitate" } })
        }
        className="space-y-6"
      >
        <TabsList className="bg-muted/60">
          <TabsTrigger value="site" className="gap-2">
            <Settings2 className="h-4 w-4" /> Setări site
          </TabsTrigger>
          <TabsTrigger value="utilizatori" className="gap-2">
            <Users className="h-4 w-4" /> Utilizatori
          </TabsTrigger>
          <TabsTrigger value="securitate" className="gap-2">
            <ShieldAlert className="h-4 w-4" /> Securitate
          </TabsTrigger>
        </TabsList>

        <TabsContent value="site">
          <SiteSettingsTab />
        </TabsContent>
        <TabsContent value="utilizatori">
          <UsersTab />
        </TabsContent>
        <TabsContent value="securitate">
          <SecurityTab />
        </TabsContent>
      </Tabs>
    </AdminPage>
  );
}

function SiteSettingsTab() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "site_settings"],
    queryFn: async () => {
      const rows = await listSettings();
      return [...rows].sort((a, b) => a.key.localeCompare(b.key));
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin", "site_settings"] });

  async function onSaveSetting(key: string, value: string) {
    try {
      await saveSetting(key, value);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Eroare");
      return;
    }
    toast.success("Salvat");
    await refresh();
  }

  async function removeSetting(key: string) {
    // Deleting a setting is not exposed by the API; clear its value instead.
    await onSaveSetting(key, "");
  }

  const grouped = useMemo(() => {
    const map: Record<string, { key: string; value: string; meta: SettingMeta }[]> = {};
    for (const row of data ?? []) {
      const meta = metaFor(row.key);
      let list = map[meta.category];
      if (!list) {
        list = [];
        map[meta.category] = list;
      }
      list.push({ key: row.key, value: row.value ?? "", meta });
    }
    return map;
  }, [data]);

  const categories = useMemo(
    () =>
      Object.keys(grouped).sort((a, b) => {
        const order = ["Site", "Eveniment", "Contact", "Înregistrare", "Avansat"];
        return order.indexOf(a) - order.indexOf(b);
      }),
    [grouped],
  );

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-8">
      {categories.map((category) => (
        <section key={category}>
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {CATEGORY_ICONS[category] ?? <Settings2 className="h-4 w-4" />}
            {category}
          </div>
          <div className="grid gap-4">
            {(grouped[category] ?? []).map(({ key, value, meta }) => (
              <SettingCard
                key={key}
                settingKey={key}
                value={value}
                meta={meta}
                onSave={onSaveSetting}
                onDelete={removeSetting}
              />
            ))}
          </div>
        </section>
      ))}

    </div>
  );
}

function SettingCard({
  settingKey,
  value,
  meta,
  onSave,
  onDelete,
}: {
  settingKey: string;
  value: string;
  meta: SettingMeta;
  onSave: (key: string, value: string) => Promise<void>;
  onDelete: (key: string) => Promise<void>;
}) {
  const [local, setLocal] = useState(value);
  const changed = local !== value;

  const handleSave = () => onSave(settingKey, local);

  return (
    <Card className="p-5 transition-shadow hover:shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-primary">{meta.icon}</span>
            <h3 className="font-semibold">{meta.label}</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{meta.description}</p>
          <p className="mt-1 text-[11px] font-mono text-muted-foreground/70">{settingKey}</p>
        </div>
        <div className="flex items-center gap-2">
          {changed && <span className="hidden text-xs text-amber-500 sm:inline">Modificat</span>}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Șterge"
            onClick={() => {
              if (window.confirm(`Ștergi setarea „${meta.label}"?`)) void onDelete(settingKey);
            }}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
          <Button size="sm" disabled={!changed} onClick={handleSave}>
            <Check className="mr-1.5 h-3.5 w-3.5" /> Salvează
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {meta.type === "boolean" ? (
          <div className="flex items-center gap-3">
            <Switch
              checked={local === "true"}
              onCheckedChange={(v) => {
                const next = v ? "true" : "false";
                setLocal(next);
                void onSave(settingKey, next);
              }}
            />
            <span className="text-sm text-muted-foreground">
              {local === "true" ? "Activat" : "Dezactivat"}
            </span>
          </div>
        ) : meta.type === "textarea" ? (
          <Textarea rows={4} value={local} onChange={(e) => setLocal(e.target.value)} />
        ) : (
          <Input type={meta.type} value={local} onChange={(e) => setLocal(e.target.value)} />
        )}
      </div>
    </Card>
  );
}

function UsersTab() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => listAdminUsers(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

  async function onCreate() {
    setBusy(true);
    try {
      await createAdminUser({ email, password });
      toast.success("Administrator creat");
      setEmail("");
      setPassword("");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Eroare la creare");
    }
    setBusy(false);
  }

  if (isLoading) return <Loading />;
  if (error) return <p className="text-sm text-destructive">{(error as Error).message}</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Conturile care pot administra site-ul. Toți utilizatorii creați aici au rol de administrator.
      </p>

      <div className="grid gap-4 rounded-xl border border-dashed border-border p-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nume@exemplu.ro" />
        </div>
        <div className="space-y-2">
          <Label>Parolă (min. 8 caractere)</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="parolă inițială"
          />
        </div>
        <Button onClick={onCreate} disabled={busy || !email || password.length < 8}>
          <Plus className="mr-2 h-4 w-4" /> Adaugă administrator
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Creat</th>
              <th className="px-4 py-3">Ultima autentificare</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((user) => (
              <tr key={user.id} className="border-t border-border">
                <td className="px-4 py-3 font-medium">{user.email}</td>
                <td className="px-4 py-3 text-muted-foreground">
                  {user.isAdmin ? "Administrator" : "Fără rol"}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {new Date(user.createdAt).toLocaleDateString("ro-RO")}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                  {user.lastSignInAt
                    ? new Date(user.lastSignInAt).toLocaleString("ro-RO", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "Niciodată"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Schimbă parola"
                      onClick={async () => {
                        const next = window.prompt(`Parolă nouă pentru ${user.email}:`);
                        if (!next) return;
                        try {
                          await setUserPassword({ userId: user.id, password: next });
                          toast.success("Parolă actualizată");
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Eroare");
                        }
                      }}
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Șterge utilizatorul"
                      onClick={async () => {
                        if (!window.confirm(`Ștergi contul ${user.email}?`)) return;
                        try {
                          await deleteAdminUser({ userId: user.id });
                          toast.success("Cont șters");
                          await refresh();
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Eroare");
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function browserOf(ua: string | null) {
  if (!ua) return "Necunoscut";
  const s = ua.toLowerCase();
  const browser = s.includes("edg/")
    ? "Edge"
    : s.includes("opr/") || s.includes("opera")
      ? "Opera"
      : s.includes("chrome") && !s.includes("chromium")
        ? "Chrome"
        : s.includes("firefox")
          ? "Firefox"
          : s.includes("safari")
            ? "Safari"
            : "Alt browser";
  const os = s.includes("windows")
    ? "Windows"
    : s.includes("android")
      ? "Android"
      : s.includes("iphone") || s.includes("ipad")
        ? "iOS"
        : s.includes("mac os")
          ? "macOS"
          : s.includes("linux")
            ? "Linux"
            : "";
  return os ? `${browser} · ${os}` : browser;
}

function fmt(date: string) {
  return new Date(date).toLocaleString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function SecurityTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [blockTarget, setBlockTarget] = useState<string | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [newIp, setNewIp] = useState("");
  const [busy, setBusy] = useState(false);

  const attempts = useQuery({ queryKey: ["admin", "login-attempts"], queryFn: () => listLoginAttempts() });
  const blocked = useQuery({ queryKey: ["admin", "blocked-ips"], queryFn: () => listBlockedIps() });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "login-attempts"] });
    await queryClient.invalidateQueries({ queryKey: ["admin", "blocked-ips"] });
  };

  const blockedSet = useMemo(
    () => new Set((blocked.data ?? []).map((b) => b.ip)),
    [blocked.data],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (attempts.data ?? []).filter((a) => {
      if (onlyFailed && a.success) return false;
      if (!q) return true;
      return [a.email, a.ip, a.user_agent, a.reason]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [attempts.data, search, onlyFailed]);

  async function doBlock(ip: string, reason: string) {
    setBusy(true);
    try {
      await blockIp({ ip, reason });
      toast.success(`IP ${ip} a fost blocat`);
      setBlockTarget(null);
      setBlockReason("");
      setNewIp("");
      await refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Eroare");
    }
    setBusy(false);
  }

  if (attempts.isLoading || blocked.isLoading) return <Loading />;
  if (attempts.error)
    return <p className="text-sm text-destructive">{(attempts.error as Error).message}</p>;

  const failed24h = (attempts.data ?? []).filter(
    (a) => !a.success && Date.now() - new Date(a.created_at).getTime() < 86_400_000,
  ).length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Vezi cine a încercat să intre în panoul de administrare, de la ce adresă IP și cu ce browser.
        Poți bloca definitiv adresele IP suspecte.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Încercări înregistrate" value={String((attempts.data ?? []).length)} icon={Globe} />
        <StatCard label="Eșuate (ultimele 24h)" value={String(failed24h)} icon={ShieldAlert} />
        <StatCard label="Adrese IP blocate" value={String((blocked.data ?? []).length)} icon={Ban} />
      </div>

      {/* IP-uri blocate */}
      <section className="space-y-4 rounded-xl border border-border bg-card p-5">
        <div>
          <h2 className="text-base font-semibold">Adrese IP blocate</h2>
          <p className="text-sm text-muted-foreground">
            Aceste adrese nu se mai pot autentifica și nu mai au acces la panoul de administrare.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-2">
            <Label>Blochează manual o adresă IP</Label>
            <Input value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="ex. 82.76.10.44" />
          </div>
          <Button disabled={busy || !newIp.trim()} onClick={() => doBlock(newIp.trim(), "Blocat manual")}>
            <Ban className="mr-2 h-4 w-4" /> Blochează
          </Button>
        </div>
        {(blocked.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nicio adresă blocată momentan.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(blocked.data ?? []).map((b) => (
              <span
                key={b.id}
                className="inline-flex items-center gap-2 rounded-full border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-sm"
              >
                <Ban className="h-3.5 w-3.5 text-destructive" />
                <span className="font-medium">{b.ip}</span>
                {b.reason ? <span className="text-xs text-muted-foreground">{b.reason}</span> : null}
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline hover:text-foreground"
                  onClick={async () => {
                    await unblockIp({ id: b.id });
                    toast.success("Deblocat");
                    await refresh();
                  }}
                >
                  deblochează
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Jurnal */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după email, IP sau browser…"
            className="sm:max-w-sm"
          />
          <div className="flex gap-2">
            <Button variant={onlyFailed ? "default" : "outline"} onClick={() => setOnlyFailed((v) => !v)}>
              Doar încercări eșuate
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Golești tot jurnalul de acces?")) return;
                await clearLoginAttempts();
                toast.success("Jurnal golit");
                await refresh();
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Golește jurnalul
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rezultat</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Browser</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Nicio înregistrare.
                  </td>
                </tr>
              ) : (
                rows.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmt(a.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{a.email || "—"}</td>
                    <td className="px-4 py-3">
                      {a.success ? (
                        <span className="inline-flex items-center gap-1.5 text-emerald-600">
                          <CheckCircle2 className="h-4 w-4" /> Reușit
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-destructive">
                          <XCircle className="h-4 w-4" /> Eșuat
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.ip || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{browserOf(a.user_agent)}</td>
                    <td className="px-4 py-3 text-right">
                      {a.ip && !blockedSet.has(a.ip) ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setBlockTarget(a.ip);
                            setBlockReason(`Încercări suspecte (${a.email || "necunoscut"})`);
                          }}
                        >
                          <Ban className="mr-2 h-4 w-4 text-destructive" /> Blochează IP
                        </Button>
                      ) : a.ip ? (
                        <span className="text-xs text-destructive">blocat</span>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={Boolean(blockTarget)} onOpenChange={(o) => !o && setBlockTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blochezi adresa {blockTarget}?</DialogTitle>
            <DialogDescription>
              Nimeni de la această adresă IP nu se va mai putea autentifica în panoul de administrare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motiv (opțional)</Label>
            <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockTarget(null)}>
              Anulează
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => blockTarget && doBlock(blockTarget, blockReason)}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />}
              Blochează IP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Globe;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </span>
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </div>
    </div>
  );
}
